import type { Patient, Practitioner, Reference } from '@medplum/fhirtypes'
import { sample } from 'lodash-es'
import type { SeenMedplumClient } from '#src/client.ts'
import type { ExtendedResourceConstructor } from '#src/ExtendedResource.ts'
import type { Logger } from '#src/logger.ts'
import { isIDTRole } from '#src/models/care-teams/constants.ts'
import { IDT } from '#src/models/care-teams/idt.ts'
import { rolesForServiceCategory } from '#src/models/healthcareService/healthcareService.ts'
import type { Order } from '#src/models/orders/index.ts'
import { Participant } from '#src/models/patients/participant.ts'
import { ExtendedPractitioner } from '#src/models/practitioner.ts'
import type { StandardTask } from '#src/models/tasks/standard/standardTask.ts'
import type { Role } from '#src/roles.ts'

type TaskOwnerArgs = {
  medplum: Pick<
    SeenMedplumClient,
    | 'readResourceClass'
    | 'searchOneResourceClass'
    | 'searchResourceClassPagesAll'
  >
  task:
    | StandardTask
    | (ExtendedResourceConstructor<StandardTask> & {
        defaultRoles: Role[]
        useServiceCategoryRoles: boolean
      })
  /**
   * The order driving this task, if any. Required so every caller has to
   * decide: order-driven tasks pass the Order (enabling service-category
   * routing); non-order tasks pass `null` explicitly.
   */
  order: Order | null
  actors?: (ExtendedPractitioner | Reference<Practitioner>)[] // list of actors involved at some stage of the workflow lifecycle
  patient?: Reference<Patient> | Participant
  context?: Reference
  logger?: Logger
}

export async function getTaskOwner({
  medplum,
  task,
  order,
  actors,
  patient,
  context,
  logger,
}: TaskOwnerArgs): Promise<ExtendedPractitioner | undefined> {
  const log = logger?.child({
    base: {
      patient: patient?.reference,
      context: context?.reference,
      task: task.constructor.name,
    },
  })

  // If the order's category maps to specific roles in the healthcare-service
  // catalog (e.g. category=massage-therapy → PT), those roles replace
  // task.defaultRoles for this assignment. The IDT-by-role pass below then
  // picks the matching member of the participant's care team. Falls back to
  // defaultRoles when the task opts out, the order has no category, or no
  // catalog entry matches. Opting out matters for tasks whose defaultRoles are
  // a hard requirement rather than a preference — an order signature is only
  // valid from a PCP no matter which service line the order belongs to.
  const overrideRoles = task.useServiceCategoryRoles
    ? getServiceCategoryRoles(order)
    : []
  const roles = overrideRoles.length > 0 ? overrideRoles : task.defaultRoles

  if (overrideRoles.length > 0) {
    log?.info(
      { overrideRoles, category: order?.category },
      'Using order-category override roles for owner assignment'
    )
  }

  const actorPractitioners = await Promise.all(
    actors?.map((a) =>
      a instanceof ExtendedPractitioner
        ? Promise.resolve(a)
        : medplum.readResourceClass(ExtendedPractitioner, a)
    ) ?? []
  )

  log?.info(
    {
      actorPractitioners: actorPractitioners.map((a) => ({
        reference: a.reference,
        roles: a.roles,
      })),
      roles,
    },
    'Found actor practitioners and roles'
  )

  const actorOwner = actorPractitioners.find((a) => a.isRole(...roles))
  if (actorOwner) {
    log?.info({ actorOwner: actorOwner.reference }, 'Found actor owner')
    return actorOwner
  }

  log?.info('Checking patient care team')

  const patientRef =
    patient instanceof Participant ? patient.reference : patient
  if (patientRef) {
    const idt = await medplum.searchOneResourceClass(
      IDT,
      IDT.byParticipant(patientRef)
    )
    if (idt) {
      for (const role of roles) {
        if (!isIDTRole(role)) {
          continue
        }

        const member = idt.getMemberByRole(role)
        if (!member) {
          continue
        }

        const practitioner = await medplum.readResourceClass(
          ExtendedPractitioner,
          member.practitioner
        )
        if (practitioner) {
          log?.info(
            { practitioner: practitioner.reference },
            'Found owner in patient care team by role'
          )
          return practitioner
        }
      }
    }
  }

  const practitioners = await medplum.searchResourceClassPagesAll(
    ExtendedPractitioner,
    ExtendedPractitioner.queryByRoles(...roles)
  )

  for (const role of roles) {
    const practitionersWithRole = practitioners.filter((p) => p.isRole(role))
    const owner = sample(practitionersWithRole)
    if (owner) {
      log?.info({ practitioner: owner.reference }, 'Found owner by role')
      return owner
    }
  }

  log?.info('No owner found')

  return undefined
}

/**
 * Maps the order's `category` (e.g. `'massage-therapy'`) to the role(s) that
 * cover that service line, by looking it up in the canonical
 * `rolesAndHealthcareServicePartials` catalog. OrderCategory codes for the
 * bookable specialties intentionally match `HEALTHCARE_SERVICE_CATEGORY` codes
 * so the catalog can be the single source of truth. Returns `[]` when the
 * order has no category or no matching entry exists.
 */
export function getServiceCategoryRoles(order: Order | null): Role[] {
  return rolesForServiceCategory(order?.category)
}
